import type { Client } from '@/types';

export function normalizeSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export function normalizeDigits(value: string): string {
  return value.replace(/\D/g, '');
}

function stripInternationalPrefix(digits: string): string[] {
  const normalized = digits.replace(/^00/, '');
  const variants = new Set<string>([digits, normalized]);

  if (normalized.startsWith('0') && normalized.length > 1) {
    variants.add(normalized.slice(1));
  }

  return [...variants].filter(Boolean);
}

export function buildPhoneSearchCandidates(raw: string, phonePrefix = '+56'): string[] {
  const digits = normalizeDigits(raw);
  const prefixDigits = normalizeDigits(phonePrefix);
  const candidates = new Set<string>();
  const digitGroups = raw
    .split(/\D+/)
    .map((group) => normalizeDigits(group))
    .filter(Boolean);

  if (digits) {
    for (const variant of stripInternationalPrefix(digits)) {
      candidates.add(variant);

      if (variant.length >= 4) {
        candidates.add(variant.slice(-4));
      }

      if (variant.length >= 8) {
        candidates.add(variant.slice(-8));
      }

      if (variant.length >= 9) {
        candidates.add(variant.slice(-9));
      }

      if (prefixDigits) {
        if (variant.startsWith(prefixDigits) && variant.length > prefixDigits.length) {
          candidates.add(variant.slice(prefixDigits.length));
        } else {
          candidates.add(`${prefixDigits}${variant}`);
        }
      }
    }
  }

  for (const group of digitGroups) {
    if (group.length >= 4) {
      candidates.add(group);
    }

    for (const variant of stripInternationalPrefix(group)) {
      if (variant.length >= 4) {
        candidates.add(variant);
      }
    }
  }

  return [...candidates].filter(Boolean);
}

type SearchableClient = Pick<Client, 'full_name' | 'phone' | 'email'>;

export function matchesClientSearch(
  client: SearchableClient,
  rawQuery: string,
  phonePrefix = '+56',
): boolean {
  const textQuery = normalizeSearchText(rawQuery);
  const digitQuery = normalizeDigits(rawQuery);
  const clientName = normalizeSearchText(client.full_name ?? '');
  const clientEmail = normalizeSearchText(client.email ?? '');
  const clientPhoneDigits = normalizeDigits(String(client.phone ?? ''));

  if (textQuery.length >= 2) {
    if (clientName.includes(textQuery) || clientEmail.includes(textQuery)) {
      return true;
    }
  }

  if (digitQuery.length < 4 || !clientPhoneDigits) {
    return false;
  }

  return buildPhoneSearchCandidates(rawQuery, phonePrefix).some((candidate) => (
    candidate.length >= 4
    && (clientPhoneDigits.includes(candidate) || clientPhoneDigits.endsWith(candidate))
  ));
}
